/**
 * useCatalogReview — the data and actions behind the Catalog Review workspace: the five lists, the selected
 * candidate, the filters (kept in the URL so a link can open a filtered view) and every review mutation. Each action
 * calls the same endpoint it always did; successes refresh every catalog list, failures show a toast.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deferCatalogCandidateMutation,
  enableDaemonMutation,
  getCatalogCandidateOptions,
  listCatalogCandidatesOptions,
  listCatalogCandidatesQueryKey,
  listCatalogItemVersionsOptions,
  listCatalogItemVersionsQueryKey,
  listCatalogReviewQueueOptions,
  listCatalogReviewQueueQueryKey,
  listCatalogSourcesOptions,
  listCatalogSourcesQueryKey,
  listCatalogVersionAdoptionsOptions,
  listCatalogVersionAdoptionsQueryKey,
  processCatalogReviewQueueMutation,
  promoteCatalogCandidateMutation,
  rejectCatalogCandidateMutation,
  restoreCatalogItemVersionMutation,
  retryCatalogCandidateReviewMutation,
  retryCatalogReviewQueueItemMutation,
  retryCatalogReviewQueueMutation,
  revertCatalogVersionAdoptionMutation,
  upgradeCatalogVersionAdoptionMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { describeQueryError, useToast } from '../../../components/ui/index.js';
import { useTabParam } from '../../../hooks/useTabParam.js';
import type { CatalogItemVersion, PromotionResult } from './catalog-types.js';
import { TARGET_TYPES, parseCsv } from './catalog-helpers.js';

export const CATALOG_TAB_IDS = ['candidates', 'sources', 'versions', 'adoptions', 'queue'] as const;
export type CatalogTabId = (typeof CATALOG_TAB_IDS)[number];

const csv = (values: string[]): string | undefined => (values.length > 0 ? values.join(',') : undefined);

/** One value goes in the singular parameter, several in the comma-separated plural one (what the server expects). */
function scopedStatus(values: string[]): { status?: string; statuses?: string } {
  return values.length === 1 ? { status: values[0] } : { statuses: csv(values) };
}

function scopedTargetType(values: string[]): { targetType?: string; targetTypes?: string } {
  return values.length === 1 ? { targetType: values[0] } : { targetTypes: csv(values) };
}

/** Matches the cached detail of any candidate (the generated key carries the operation id). */
function isCandidateDetailKey(queryKey: readonly unknown[]): boolean {
  const head = queryKey[0];
  return typeof head === 'object' && head !== null && '_id' in head && head._id === 'getCatalogCandidate';
}

export function useCatalogReview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useTabParam(CATALOG_TAB_IDS, 'candidates');
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [lastPromotion, setLastPromotion] = useState<PromotionResult | null>(null);

  // Filters live in the URL: ?status=… / ?statuses=a,b and ?targetType=… / ?targetTypes=a,b.
  const status = searchParams.get('status') ?? '';
  const targetType = searchParams.get('targetType') ?? '';
  const activeStatuses = status ? [status] : parseCsv(searchParams.get('statuses'));
  const activeTargetTypes = targetType ? [targetType] : parseCsv(searchParams.get('targetTypes'));

  const candidatesQuery = useQuery({
    ...listCatalogCandidatesOptions({
      query: {
        ...scopedStatus(activeStatuses),
        ...scopedTargetType(activeTargetTypes),
        limit: '150',
      },
    }),
    refetchInterval: 5_000,
  });
  const queueQuery = useQuery({
    ...listCatalogReviewQueueOptions({ query: { limit: '50' } }),
    refetchInterval: 5_000,
  });
  const sourcesQuery = useQuery({
    ...listCatalogSourcesOptions({ query: { limit: '150' } }),
    refetchInterval: 10_000,
  });
  const versionsQuery = useQuery({
    ...listCatalogItemVersionsOptions({ query: { ...scopedTargetType(activeTargetTypes), limit: '150' } }),
    refetchInterval: 10_000,
  });
  const adoptionsQuery = useQuery({ ...listCatalogVersionAdoptionsOptions(), refetchInterval: 10_000 });
  const candidates = candidatesQuery.data?.data ?? [];
  // Until a candidate is picked, the first one of the inbox is open.
  const openId = selectedId ?? candidates[0]?.id ?? null;
  const selectedQuery = useQuery({
    ...getCatalogCandidateOptions({ path: { id: String(openId ?? 0) } }),
    enabled: openId != null,
  });

  const versions = versionsQuery.data?.data ?? [];
  const targetTypes = Array.from(
    new Set<string>([...TARGET_TYPES, ...candidates.map((c) => c.targetType), ...versions.map((v) => v.targetType)]),
  ).sort();

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: listCatalogCandidatesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listCatalogReviewQueueQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listCatalogSourcesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listCatalogItemVersionsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listCatalogVersionAdoptionsQueryKey() }),
      queryClient.invalidateQueries({ predicate: (query) => isCandidateDetailKey(query.queryKey) }),
    ]);
  const onError = (error: unknown) => notify(describeQueryError(error, 'the catalog action'), 'danger');
  const done = { onSuccess: () => void refresh(), onError };

  const promote = useMutation({
    ...promoteCatalogCandidateMutation(),
    onSuccess: (result) => {
      setLastPromotion(result.data);
      void refresh();
    },
    onError,
  });
  const restoreVersion = useMutation({
    ...restoreCatalogItemVersionMutation(),
    onSuccess: (result) => {
      setLastPromotion(result.data);
      void refresh();
    },
    onError,
  });
  const upgradeAdoption = useMutation({ ...upgradeCatalogVersionAdoptionMutation(), ...done });
  const revertAdoption = useMutation({ ...revertCatalogVersionAdoptionMutation(), ...done });
  const reject = useMutation({ ...rejectCatalogCandidateMutation(), ...done });
  const defer = useMutation({ ...deferCatalogCandidateMutation(), ...done });
  const retryCandidateReview = useMutation({ ...retryCatalogCandidateReviewMutation(), ...done });
  const enableDaemon = useMutation(enableDaemonMutation());
  const retryQueueItem = useMutation({ ...retryCatalogReviewQueueItemMutation(), ...done });
  const retryQueue = useMutation({ ...retryCatalogReviewQueueMutation(), ...done });
  const processQueue = useMutation({ ...processCatalogReviewQueueMutation(), ...done });

  const idPath = (id: number) => ({ path: { id: String(id) } });

  const actions = {
    promote: (id: number) => promote.mutate(idPath(id)),
    reject: (id: number) => reject.mutate({ ...idPath(id), body: {} }),
    defer: (id: number) => defer.mutate({ ...idPath(id), body: {} }),
    retryReview: (id: number) => retryCandidateReview.mutate(idPath(id)),
    restoreVersion: (version: CatalogItemVersion) =>
      restoreVersion.mutate({
        ...idPath(version.id),
        body: { reason: `Restored from Catalog Review v${version.version}` },
      }),
    upgrade: (consumerKey: string) => upgradeAdoption.mutate({ body: { consumerKey } }),
    revert: (consumerKey: string) => revertAdoption.mutate({ body: { consumerKey } }),
    retryItem: (id: number) => retryQueueItem.mutate(idPath(id)),
    retryQueue: (targetDaemonName?: string) => retryQueue.mutate({ body: { targetDaemonName, processNow: true } }),
    processQueue: (targetDaemonName?: string) => processQueue.mutate({ body: { targetDaemonName } }),
    /** Enable the review daemon, then retry its queue right away. */
    enableAndRetry: async (daemonId: number, daemonName: string) => {
      try {
        await enableDaemon.mutateAsync(idPath(daemonId));
        await retryQueue.mutateAsync({ body: { targetDaemonName: daemonName, processNow: true } });
      } catch (error) {
        onError(error instanceof Error ? error : new Error('The request failed'));
      }
    },
  };

  const editParams = (change: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    change(next);
    setSearchParams(next, { replace: true });
  };

  const filters = {
    status: activeStatuses.length === 1 ? activeStatuses[0] : '',
    targetType: activeTargetTypes.length === 1 ? activeTargetTypes[0] : '',
    activeStatuses,
    activeTargetTypes,
    targetTypes,
    setStatus: (value: string) =>
      editParams((next) => {
        if (value) next.set('status', value);
        else next.delete('status');
        next.delete('statuses');
      }),
    setTargetType: (value: string) =>
      editParams((next) => {
        if (value) next.set('targetType', value);
        else next.delete('targetType');
        next.delete('targetTypes');
      }),
    clearStatuses: () =>
      editParams((next) => {
        next.delete('status');
        next.delete('statuses');
      }),
    clearTargetTypes: () =>
      editParams((next) => {
        next.delete('targetType');
        next.delete('targetTypes');
      }),
    /** The summary's "Review findings" shortcut: candidates tab filtered to the failed statuses. */
    showStatuses: (values: string[]) =>
      editParams((next) => {
        next.set('tab', 'candidates');
        next.delete('status');
        next.set('statuses', values.join(','));
      }),
  };

  return {
    tab,
    setTab,
    queries: {
      candidates: candidatesQuery,
      queue: queueQuery,
      sources: sourcesQuery,
      versions: versionsQuery,
      adoptions: adoptionsQuery,
      selected: selectedQuery,
    },
    selectedId: openId,
    selectCandidate: setSelectedId,
    lastPromotion,
    actions,
    filters,
    busy: {
      candidate: promote.isPending || retryCandidateReview.isPending || reject.isPending || defer.isPending,
      queue: enableDaemon.isPending || retryQueueItem.isPending || retryQueue.isPending || processQueue.isPending,
      summary: retryQueue.isPending || processQueue.isPending,
      restoringVersionId: restoreVersion.isPending ? Number(restoreVersion.variables?.path.id) : null,
      upgradingKey: upgradeAdoption.isPending ? (upgradeAdoption.variables?.body?.consumerKey ?? null) : null,
      revertingKey: revertAdoption.isPending ? (revertAdoption.variables?.body?.consumerKey ?? null) : null,
    },
  };
}
